import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import UserAvatar from "@/components/UserAvatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import DriverBottomNav from "@/components/DriverBottomNav";
import { fmt12 } from "@/lib/time";
import { ArrowLeft, Calendar, Clock, MapPin, Phone, MessageSquare, CheckCircle, XCircle, Navigation, User, Map, Plus, Trash2, Pencil, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { openMaps, MapsProvider } from "@/lib/mapsUtils";
import EditBookingDialog from "@/components/EditBookingDialog";
import DeleteCustomerDialog from "@/components/DeleteCustomerDialog";
import BookingEditLog from "@/components/BookingEditLog";

import { API } from "@/lib/api";


export default function Bookings() {
  const navigate = useNavigate();
  const { isAdmin, user, loading: authLoading } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pending");
  const [settings, setSettings] = useState({ preferred_maps: MapsProvider.GOOGLE });
  const [editingStopsFor, setEditingStopsFor] = useState(null); // booking.id
  const [stopsDraft, setStopsDraft] = useState([]); // [{address, coords}]
  const [savingStops, setSavingStops] = useState(false);
  // Admin booking-edit dialog (price/address/schedule/payment etc.)
  const [editingBooking, setEditingBooking] = useState(null);
  // Reassign-trip dialog state: when set, shows a picker for which user the
  // trip should be claimed under. Sole-operator owners run as admin + driver
  // logins, and bookings can end up claimed under the "wrong" one.
  const [reassigningBooking, setReassigningBooking] = useState(null);
  const [reassignTargets, setReassignTargets] = useState([]); // list of {id, label}
  const [reassigning, setReassigning] = useState(false);

  const openReassign = useCallback(async (booking) => {
    setReassigningBooking(booking);
    try {
      // Drivers list (admins are included via /admin/drivers?status=approved
      // returns drivers — admins fetched separately).
      const [driversRes, adminsRes] = await Promise.all([
        axios.get(`${API}/admin/drivers`),
        axios.get(`${API}/admin/customers?role=admin`).catch(() => ({ data: [] })),
      ]);
      const drivers = (driversRes.data || []).map((u) => ({
        id: u.id,
        label: `${u.first_name || ""} ${u.last_name || ""}`.trim() + ` · driver${u.driver_status ? ` (${u.driver_status})` : ""}`,
      }));
      const admins = (adminsRes.data || []).filter((u) => u.role === "admin").map((u) => ({
        id: u.id,
        label: `${u.first_name || ""} ${u.last_name || ""}`.trim() + " · admin",
      }));
      setReassignTargets([...admins, ...drivers]);
    } catch {
      setReassignTargets([]);
    }
  }, []);

  const submitReassign = async (newDriverId) => {
    if (!reassigningBooking) return;
    setReassigning(true);
    try {
      await axios.post(`${API}/admin/bookings/${reassigningBooking.id}/reassign`, {
        driver_id: newDriverId || "",
      });
      toast.success(newDriverId ? "Trip reassigned" : "Trip unassigned");
      setReassigningBooking(null);
      fetchBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reassign failed");
    } finally {
      setReassigning(false);
    }
  };

  const HISTORY_PAGE_SIZE = 25;

  const fetchBookings = useCallback(async () => {
    // Fetch ACTIVE bookings (pending/confirmed/in_progress) plus the first
    // page of history in parallel. Active bookings are always few enough
    // to fit in one page (200 cap on the server), so no pagination there.
    // History is paginated so drivers with hundreds of past rides don't
    // pull a 5MB payload every time they open the Rides tab.
    try {
      const [activeRes, historyRes, settingsRes] = await Promise.all([
        axios.get(`${API}/bookings`, { params: { active: 1, page: 1, page_size: 200 } }),
        axios.get(`${API}/bookings`, { params: { page: 1, page_size: HISTORY_PAGE_SIZE } }),
        axios.get(`${API}/driver/settings`),
      ]);
      const active = activeRes.data?.items || [];
      const historyItems = (historyRes.data?.items || []).filter(
        (b) => b.status === "completed" || b.status === "cancelled" || b.status === "no_show"
      );
      // Dedupe by id — active may be a subset of the newest-page returned
      // by the un-filtered history query. We want the union.
      const seen = new Set(active.map((b) => b.id));
      const merged = active.concat(historyItems.filter((b) => !seen.has(b.id)));
      setBookings(merged);
      setHistoryPage(1);
      setHistoryHasMore(!!historyRes.data?.has_more);
      setHistoryTotal(historyRes.data?.total || 0);
      setSettings(settingsRes.data);
    } catch (e) {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMoreHistory = useCallback(async () => {
    if (historyLoading || !historyHasMore) return;
    setHistoryLoading(true);
    try {
      const nextPage = historyPage + 1;
      const { data } = await axios.get(`${API}/bookings`, {
        params: { page: nextPage, page_size: HISTORY_PAGE_SIZE },
      });
      const older = (data?.items || []).filter(
        (b) => b.status === "completed" || b.status === "cancelled" || b.status === "no_show"
      );
      setBookings((prev) => {
        const seen = new Set(prev.map((b) => b.id));
        return prev.concat(older.filter((b) => !seen.has(b.id)));
      });
      setHistoryPage(nextPage);
      setHistoryHasMore(!!data?.has_more);
    } catch {
      toast.error("Couldn't load more history");
    } finally {
      setHistoryLoading(false);
    }
  }, [historyLoading, historyHasMore, historyPage]);

  useEffect(() => {
    // Wait for AuthContext to hydrate the token before hitting the now
    // auth-gated /api/bookings endpoint. Otherwise the first fetch fires
    // before axios has the Bearer header and we eat a spurious 401 +
    // "Failed to load bookings" toast on every page mount.
    if (authLoading) return;
    if (!user) return;
    fetchBookings();
  }, [fetchBookings, authLoading, user]);

  // Deep-link into the Reassign dialog from `?reassign={bookingId}`.
  // Used by the TrackingPage push-notification redirect: if an admin
  // tapped a "New ride request" notification for a trip that's already
  // claimed, we send them to `/driver/bookings?reassign={id}` and open
  // the reassign dialog automatically once the booking list has loaded.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const wanted = params.get('reassign');
    if (!wanted || bookings.length === 0) return;
    if (reassigningBooking?.id === wanted) return;
    const target = bookings.find((b) => b.id === wanted);
    if (target) {
      openReassign(target);
      // Clean the query param so the dialog doesn't re-open on refresh.
      params.delete('reassign');
      const q = params.toString();
      const clean = `${window.location.pathname}${q ? `?${q}` : ''}`;
      window.history.replaceState(null, '', clean);
    }
  }, [bookings, openReassign, reassigningBooking?.id]);

  const confirmBooking = async (id) => {
    try { await axios.post(`${API}/bookings/${id}/confirm`); toast.success("Confirmed!"); fetchBookings(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to confirm"); }
  };
  const cancelBooking = async (id) => {
    try { await axios.post(`${API}/bookings/${id}/cancel`); toast.success("Cancelled"); fetchBookings(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel"); }
  };
  const completeBooking = async (id) => {
    try { await axios.post(`${API}/bookings/${id}/complete`); toast.success("Completed!"); fetchBookings(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to complete"); }
  };
  const noShowBooking = async (id) => {
    if (!window.confirm("Mark this booking as a no-show? It will be closed out without payment.")) return;
    try { await axios.post(`${API}/bookings/${id}/no-show`); toast.success("Marked as no-show"); fetchBookings(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to mark no-show"); }
  };
  const startTrip = async (booking) => {
    try {
      // Claim the booking so the driver can broadcast GPS for it.
      // Critical: we must confirm the booking actually got our driver_id
      // before navigating, otherwise the TrackingPage shows the map but the
      // driver action bar never appears (isDriver requires driver_id match).
      let claimed = booking;
      if (!booking.driver_id) {
        const { data } = await axios.post(`${API}/driver/accept-booking/${booking.id}`);
        claimed = data || booking;
        if (!claimed.driver_id) {
          toast.error("Claim didn't stick — please try again or refresh");
          return;
        }
      }
      toast.success(`Trip claimed — opening live tracking`);
      navigate(`/driver/track/${booking.id}`);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      if (status === 400 && /already assigned/i.test(detail)) {
        // Already ours — proceed
        navigate(`/driver/track/${booking.id}`);
      } else if (status === 403) {
        toast.error(`Can't claim this trip — ${detail}`);
      } else {
        toast.error(`Could not start trip: ${detail || "unknown error"}`);
      }
    }
  };

  const navigateTo = (booking, type) => {
    const isPickup = type === 'pickup';
    const addr = isPickup ? booking.pickup_address : booking.dropoff_address;
    const coords = (isPickup ? booking.pickup_coords : booking.dropoff_coords) || {};
    openMaps(settings.preferred_maps, {
      lat: typeof coords.lat === 'number' ? coords.lat : null,
      lng: typeof coords.lng === 'number' ? coords.lng : null,
      address: addr,
    });
  };

  const openStopsEditor = (booking) => {
    const existing = (booking.stops || []).map(s => ({ address: s.address || "", coords: s.coords || null }));
    setStopsDraft(existing.length ? existing : [{ address: "", coords: null }]);
    setEditingStopsFor(booking.id);
  };
  const cancelStopsEditor = () => {
    setEditingStopsFor(null);
    setStopsDraft([]);
  };
  const saveStops = async (bookingId) => {
    const valid = stopsDraft.filter(s => s.address && s.coords);
    const missing = stopsDraft.filter(s => s.address && !s.coords);
    if (missing.length) {
      toast.error("Pick each stop from the dropdown so we get GPS coords.");
      return;
    }
    setSavingStops(true);
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}/stops`, {
        stops: valid.map(s => ({ address: s.address, coords: s.coords })),
      });
      toast.success(valid.length === 0 ? "Stops cleared" : `Saved ${valid.length} stop${valid.length === 1 ? "" : "s"}`);
      cancelStopsEditor();
      fetchBookings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save stops");
    } finally {
      setSavingStops(false);
    }
  };

  // Auto-hide pending bookings whose scheduled time is already in the past —
  // those are stale/no-shows the driver should action via the No-show button.
  // We keep "asap" rides + bookings without a date in the active list.
  //
  // 🐛 TIMEZONE FIX (2026-07-31): `new Date("2026-07-31")` parses YYYY-MM-DD
  // strings as UTC midnight, which in any negative-UTC-offset zone (all of
  // the US, Canada, LatAm) renders as the PREVIOUS calendar day in local
  // time. That's how a booking scheduled for TODAY got shoved into the
  // stale-pending "moved to History" banner. We now parse the date string
  // as LOCAL midnight and honor scheduled_time with a 1-hour grace window
  // (drivers running a few minutes late don't spontaneously get their trip
  // buried in history).
  const GRACE_MINUTES = 60;
  const isFutureOrToday = (b) => {
    if (b.asap) return true;
    if (!b.scheduled_date) return true;
    try {
      const parts = String(b.scheduled_date).split('-').map(Number);
      if (parts.length !== 3 || parts.some((n) => !Number.isFinite(n))) return true;
      const [y, m, d] = parts;
      const sd = new Date(y, m - 1, d);  // LOCAL midnight

      // If the booking has a scheduled_time, check whether that specific
      // slot (plus grace) is still in the future. This is the accurate
      // check — a 3pm ride at 4pm today is stale, a 9pm ride at 4pm today
      // is still upcoming.
      if (b.scheduled_time && typeof b.scheduled_time === 'string') {
        const [hh, mm] = b.scheduled_time.split(':').map(Number);
        if (Number.isFinite(hh) && Number.isFinite(mm)) {
          sd.setHours(hh, mm, 0, 0);
          return sd.getTime() >= Date.now() - GRACE_MINUTES * 60 * 1000;
        }
      }

      // No time provided — treat as "any time today or later".
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return sd >= today;
    } catch {
      return true;
    }
  };

  // Service-type filter chips
  const [serviceFilter, setServiceFilter] = useState("all");
  const filtered = bookings.filter(b => serviceFilter === "all" ? true : b.service_type === serviceFilter);

  // Sort History (completed/cancelled/no_show) newest-first by scheduled
  // date+time. Falls back to created_at if scheduled fields are missing.
  const bookingDate = (b) => {
    try {
      if (b.scheduled_date) {
        const t = b.scheduled_time || "00:00";
        const d = new Date(`${b.scheduled_date}T${t}`);
        if (!isNaN(d.getTime())) return d.getTime();
      }
      if (b.completed_at) {
        const d = new Date(b.completed_at);
        if (!isNaN(d.getTime())) return d.getTime();
      }
      if (b.created_at) {
        const d = new Date(b.created_at);
        if (!isNaN(d.getTime())) return d.getTime();
      }
    } catch { /* fall through */ }
    return 0;
  };

  const pendingBookings = filtered
    .filter(b => b.status === "pending" && isFutureOrToday(b))
    .sort((a, b) => bookingDate(a) - bookingDate(b));  // oldest-first here so soonest upcoming trip is on top
  const stalePending = filtered
    .filter(b => b.status === "pending" && !isFutureOrToday(b))
    .sort((a, b) => bookingDate(b) - bookingDate(a));
  const confirmedBookings = filtered
    .filter(b => ["confirmed", "in_progress"].includes(b.status))
    .sort((a, b) => bookingDate(a) - bookingDate(b));  // soonest ride first — matches the actual workflow
  const completedBookings = filtered
    .filter(b => ["completed", "cancelled", "no_show"].includes(b.status))
    .sort((a, b) => bookingDate(b) - bookingDate(a));  // newest-first for history browsing

  const getStatusBadge = (status) => {
    const styles = {
      pending: "bg-[#0047FF]/10 text-[#4D88FF] border-[#0047FF]/25",
      confirmed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      in_progress: "bg-[#0047FF]/20 text-[#7AADFF] border-[#0047FF]/30",
      completed: "bg-zinc-800 text-zinc-400 border-white/10",
      cancelled: "bg-red-500/10 text-red-400 border-red-500/20",
      no_show: "bg-amber-500/10 text-amber-400 border-amber-500/20"
    };
    const label = status === "in_progress" ? "Live" : status === "no_show" ? "No-show" : status.charAt(0).toUpperCase() + status.slice(1);
    return <Badge className={styles[status] || ""}>{label}</Badge>;
  };

  const BookingCard = ({ booking }) => (
    <Card className="bg-[#121212] border-white/5">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <UserAvatar
              user={{
                first_name: booking.customer_name?.split(' ')[0],
                last_name: booking.customer_name?.split(' ').slice(1).join(' '),
                email: booking.customer_phone,
                avatar_url: booking.customer_avatar_url,
              }}
              size={44}
              ring="ring-2 ring-white/5"
              testId={`booking-customer-avatar-${booking.id}`}
            />
            <div>
              <h3 className="font-semibold text-white">{booking.customer_name}</h3>
              <p className="text-sm text-zinc-500">{booking.customer_phone}</p>
            </div>
          </div>
          {getStatusBadge(booking.status)}
        </div>
        {isAdmin && (
          <div className="mb-3 flex items-center justify-between gap-2 flex-wrap rounded-md bg-zinc-800/40 px-3 py-2 border border-white/5">
            <div className="text-xs">
              <span className="text-zinc-500">Total · </span>
              <span className="text-white font-bold">${(booking.estimated_price || 0).toFixed(2)}</span>
              {booking.distance_miles != null && (
                <span className="text-zinc-500"> · {booking.distance_miles.toFixed?.(1) ?? booking.distance_miles} mi</span>
              )}
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => openReassign(booking)}
                className="h-7 px-2 text-[11px] border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                data-testid={`reassign-booking-${booking.id}`}
              >
                <User className="w-3 h-3 mr-1" /> Reassign
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={() => setEditingBooking(booking)}
                className="h-7 px-3 text-[11px] font-bold bg-[#0047FF] hover:bg-[#003BE0] text-white"
                data-testid={`edit-booking-${booking.id}`}
              >
                <Pencil className="w-3 h-3 mr-1" /> Edit time / pickup
              </Button>
            </div>
          </div>
        )}
        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 text-zinc-500" />
            <span className="text-zinc-300">{booking.scheduled_date}</span>
            <Clock className="w-4 h-4 text-zinc-500 ml-2" />
            <span className="text-zinc-300">{fmt12(booking.scheduled_time)}</span>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setEditingBooking(booking)}
                className="ml-auto text-[10px] uppercase tracking-wider font-bold text-[#7AADFF] hover:text-white flex items-center gap-1"
                data-testid={`edit-time-inline-${booking.id}`}
                title="Edit scheduled time"
              >
                <Pencil className="w-3 h-3" /> Change
              </button>
            )}
          </div>
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-emerald-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-xs text-zinc-500">Pickup</p>
              <p className="text-sm text-zinc-300">{booking.pickup_address}</p>
            </div>
            {isAdmin && (
              <button
                type="button"
                onClick={() => setEditingBooking(booking)}
                className="text-[10px] uppercase tracking-wider font-bold text-emerald-300 hover:text-white flex items-center gap-1"
                data-testid={`edit-pickup-inline-${booking.id}`}
                title="Edit pickup address"
              >
                <Pencil className="w-3 h-3" /> Change
              </button>
            )}
          </div>
          {Array.isArray(booking.stops) && booking.stops.length > 0 && booking.stops.map((stop, idx) => (
            <div key={idx} className="flex items-start gap-2" data-testid={`booking-stop-${idx}`}>
              <MapPin className="w-4 h-4 text-amber-400 mt-0.5" />
              <div>
                <p className="text-xs text-amber-400 font-semibold">Stop {idx + 1}</p>
                <p className="text-sm text-zinc-300">{stop.address}</p>
              </div>
            </div>
          ))}
          <div className="flex items-start gap-2">
            <MapPin className="w-4 h-4 text-[#4D88FF] mt-0.5" />
            <div><p className="text-xs text-zinc-500">Dropoff</p><p className="text-sm text-zinc-300">{booking.dropoff_address}</p></div>
          </div>
          {isAdmin && editingStopsFor !== booking.id && (
            <button
              onClick={() => openStopsEditor(booking)}
              data-testid={`edit-stops-${booking.id}`}
              className="text-[11px] text-amber-300 hover:text-amber-200 font-semibold flex items-center gap-1 pt-1"
            >
              <Plus className="w-3 h-3" /> {booking.stops?.length ? "Edit stops" : "Add stops to this trip"}
            </button>
          )}
          {isAdmin && editingStopsFor === booking.id && (
            <div className="border border-amber-500/30 rounded-lg p-3 bg-amber-500/5 space-y-3" data-testid={`stops-editor-${booking.id}`}>
              <p className="text-[10px] uppercase tracking-wider font-bold text-amber-300">
                Add stops (up to 4) — pick each from the dropdown
              </p>
              {stopsDraft.map((stop, idx) => (
                <div key={idx} className="space-y-1.5" data-testid={`stops-editor-row-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">Stop {idx + 1}</span>
                    <button
                      onClick={() => setStopsDraft(stopsDraft.filter((_, i) => i !== idx))}
                      className="text-[10px] text-white/40 hover:text-red-400 flex items-center gap-1"
                      data-testid={`stops-editor-remove-${idx}`}
                    >
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  </div>
                  <AddressAutocomplete
                    value={stop.address}
                    onChange={(address, coords) => {
                      const next = [...stopsDraft];
                      next[idx] = { address, coords };
                      setStopsDraft(next);
                    }}
                    placeholder={`Stop ${idx + 1} address`}
                    accent="amber"
                    testId={`stops-editor-input-${idx}`}
                    anchor={booking.pickup_coords || null}
                  />
                  {stop.address && !stop.coords && (
                    <p className="text-[10px] text-amber-400/90">⚠ Pick a suggestion from the dropdown</p>
                  )}
                </div>
              ))}
              {stopsDraft.length < 4 && (
                <button
                  onClick={() => setStopsDraft([...stopsDraft, { address: "", coords: null }])}
                  data-testid={`stops-editor-add-${booking.id}`}
                  className="w-full text-[11px] text-amber-300 hover:text-amber-200 font-semibold flex items-center justify-center gap-1 py-1.5 rounded border border-dashed border-amber-500/30 hover:border-amber-500/50"
                >
                  <Plus className="w-3 h-3" /> Add another stop ({stopsDraft.length}/4)
                </button>
              )}
              <div className="flex gap-2 pt-1">
                <Button
                  onClick={() => saveStops(booking.id)}
                  disabled={savingStops}
                  className="flex-1 h-8 bg-amber-500 hover:bg-amber-400 text-black text-xs font-bold"
                  data-testid={`save-stops-${booking.id}`}
                >
                  {savingStops ? "Saving…" : "Save stops"}
                </Button>
                <Button
                  onClick={cancelStopsEditor}
                  variant="outline"
                  className="flex-1 h-8 border-white/15 text-zinc-300 hover:bg-white/5 text-xs"
                  data-testid={`cancel-stops-${booking.id}`}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
          {booking.notes && <p className="text-sm text-zinc-500 italic">"{booking.notes}"</p>}
        </div>
        {isAdmin && <BookingEditLog bookingId={booking.id} />}
        {booking.status === "pending" && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Button onClick={() => confirmBooking(booking.id)} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black">
                <CheckCircle className="w-4 h-4 mr-2" />Confirm
              </Button>
              <Button onClick={() => cancelBooking(booking.id)} variant="outline" className="flex-1 border-red-500/30 text-red-400 hover:bg-red-500/10">
                <XCircle className="w-4 h-4 mr-2" />Decline
              </Button>
            </div>
            <Button
              onClick={() => noShowBooking(booking.id)}
              variant="outline"
              size="sm"
              className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
              data-testid={`no-show-pending-${booking.id}`}
            >
              No-show / Customer didn't appear
            </Button>
          </div>
        )}
        {(booking.status === "pending" || booking.status === "confirmed" || booking.status === "in_progress") && (
          <div className="space-y-2">
            <Button
              onClick={() => startTrip(booking)}
              className="w-full bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-xl shadow-[0_4px_24px_-8px_rgba(0,71,255,0.6)] border-0"
              data-testid={`track-trip-${booking.id}`}
            >
              <Map className="w-4 h-4 mr-2" />
              {booking.status === "in_progress"
                ? "Resume Live Track"
                : booking.status === "pending"
                  ? "Take this trip"
                  : "Start Trip & Live Track"}
            </Button>
            <div className="flex gap-2">
              <Button onClick={() => navigateTo(booking, 'pickup')} className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-zinc-200">
                <Navigation className="w-4 h-4 mr-2" />Navigate Pickup
              </Button>
              <Button onClick={() => navigateTo(booking, 'dropoff')} variant="outline" className="flex-1 border-white/10 text-zinc-300 hover:bg-zinc-800">
                <Navigation className="w-4 h-4 mr-2" />Navigate Drop
              </Button>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => window.open(`tel:${booking.customer_phone}`, '_self')} variant="outline" size="sm" className="flex-1 border-white/10 text-zinc-300 hover:bg-zinc-800">
                <Phone className="w-4 h-4 mr-1" />Call
              </Button>
              <Button onClick={() => window.open(`sms:${booking.customer_phone}`, '_self')} variant="outline" size="sm" className="flex-1 border-white/10 text-zinc-300 hover:bg-zinc-800">
                <MessageSquare className="w-4 h-4 mr-1" />Text
              </Button>
              <Button onClick={() => completeBooking(booking.id)} size="sm" className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black">
                <CheckCircle className="w-4 h-4 mr-1" />Done
              </Button>
            </div>
            <Button
              onClick={() => noShowBooking(booking.id)}
              variant="outline"
              size="sm"
              className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10"
              data-testid={`no-show-${booking.id}`}
            >
              No-show / Customer didn't appear
            </Button>
            <Button
              onClick={() => {
                if (!window.confirm("Cancel this booking? The customer will be notified.")) return;
                cancelBooking(booking.id);
              }}
              variant="outline"
              size="sm"
              className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10"
              data-testid={`cancel-${booking.id}`}
            >
              <XCircle className="w-4 h-4 mr-2" />
              Cancel booking
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="h-[100dvh] flex flex-col bg-[#050505]">
      <header className="flex-shrink-0 z-10 bg-[#050505]/95 backdrop-blur border-b border-white/5">
        <div className="px-5 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchBookings}
            className="text-zinc-400 hover:text-white hover:bg-zinc-800"
            data-testid="bookings-refresh-button"
            title="Refresh bookings"
          >
            <RefreshCw className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-white">Scheduled Rides</h1>
            <p className="text-sm text-zinc-500">Customer bookings</p>
          </div>
          {isAdmin && (
            <Button
              onClick={() => navigate("/driver/new-booking")}
              size="sm"
              className="bg-[#0047FF] hover:bg-[#003BE0] text-white font-bold rounded-full px-3"
              data-testid="new-booking-button"
            >
              <Plus className="w-4 h-4 mr-1" />
              New
            </Button>
          )}
        </div>
      </header>
      <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 py-6 pb-24">
        {/* Service-type filter chips — let admin see just rides or just packages */}
        <div className="flex gap-1.5 mb-4 overflow-x-auto -mx-1 px-1 pb-1" data-testid="service-type-filters">
          {[
            { id: "all",            label: "All" },
            { id: "ride",           label: "Rides" },
            { id: "envelope",       label: "Envelope" },
            { id: "small_package",  label: "Small pkg" },
            { id: "medium_package", label: "Medium pkg" },
          ].map(opt => (
            <button
              key={opt.id}
              onClick={() => setServiceFilter(opt.id)}
              className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border whitespace-nowrap transition-colors ${
                serviceFilter === opt.id
                  ? "bg-[#0047FF] border-[#0047FF] text-white"
                  : "bg-transparent border-white/15 text-zinc-400 hover:bg-white/5"
              }`}
              data-testid={`service-filter-${opt.id}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="w-full bg-[#121212] border border-white/5 p-1 mb-6">
            <TabsTrigger value="pending" className="flex-1 data-[state=active]:bg-zinc-800 data-[state=active]:text-white">Pending ({pendingBookings.length})</TabsTrigger>
            <TabsTrigger value="confirmed" className="flex-1 data-[state=active]:bg-zinc-800 data-[state=active]:text-white">Confirmed ({confirmedBookings.length})</TabsTrigger>
            <TabsTrigger value="history" className="flex-1 data-[state=active]:bg-zinc-800 data-[state=active]:text-white">History</TabsTrigger>
          </TabsList>
          <TabsContent value="pending" className="space-y-4">
            {loading ? <div className="text-center py-12 text-zinc-500">Loading...</div> : pendingBookings.length === 0 ? <div className="text-center py-12"><Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-4" /><p className="text-zinc-500">No pending bookings</p></div> : pendingBookings.map(b => <BookingCard key={b.id} booking={b} />)}
          </TabsContent>
          <TabsContent value="confirmed" className="space-y-4">
            {loading ? <div className="text-center py-12 text-zinc-500">Loading...</div> : confirmedBookings.length === 0 ? <div className="text-center py-12"><CheckCircle className="w-12 h-12 text-zinc-700 mx-auto mb-4" /><p className="text-zinc-500">No confirmed bookings</p></div> : confirmedBookings.map(b => <BookingCard key={b.id} booking={b} />)}
          </TabsContent>
          <TabsContent value="history" className="space-y-4">
            {/* Stale-pending banner sits at the top of History so it renders
                right next to the affected cards below. Previously lived on
                the Pending tab which was confusing — banner claimed the
                bookings had moved to History, but the banner itself stayed
                behind. */}
            {stalePending.length > 0 && (
              <div className="rounded-lg bg-amber-500/5 border border-amber-500/20 p-3 text-xs text-amber-300" data-testid="stale-pending-banner">
                <strong>{stalePending.length}</strong> pending booking{stalePending.length === 1 ? '' : 's'} {stalePending.length === 1 ? 'is' : 'are'} past their scheduled time — mark them as <em>No-show</em> or <em>Completed</em> below if you handled them offline.
              </div>
            )}
            {loading ? <div className="text-center py-12 text-zinc-500">Loading...</div> : (completedBookings.length === 0 && stalePending.length === 0) ? <div className="text-center py-12"><Clock className="w-12 h-12 text-zinc-700 mx-auto mb-4" /><p className="text-zinc-500">No history</p></div> : (
              <>
                {stalePending.map(b => <BookingCard key={b.id} booking={b} />)}
                {completedBookings.map(b => <BookingCard key={b.id} booking={b} />)}
                {historyHasMore && (
                  <div className="pt-2 text-center">
                    <Button
                      onClick={loadMoreHistory}
                      disabled={historyLoading}
                      variant="outline"
                      className="border-white/10 text-zinc-300 hover:bg-zinc-800 rounded-xl"
                      data-testid="load-more-history"
                    >
                      {historyLoading ? "Loading…" : `Load more (${Math.max(historyTotal - completedBookings.length, 0)} left)`}
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </main>
      <DriverBottomNav />
      <EditBookingDialog
        booking={editingBooking}
        open={!!editingBooking}
        onClose={() => setEditingBooking(null)}
        onSaved={(_updated, ctx) => {
          fetchBookings();
          if (!ctx || !ctx.undoPatch || Object.keys(ctx.undoPatch).length === 0) return;
          // 6-second Undo affordance — rehydrate the previous values in a
          // single PATCH if the owner realises they touched the wrong booking.
          toast("Booking updated", {
            description: `${ctx.changedFields.length} field${ctx.changedFields.length === 1 ? "" : "s"} changed`,
            action: {
              label: "Undo",
              onClick: async () => {
                try {
                  await axios.patch(`${API}/admin/bookings/${ctx.bookingId}`, ctx.undoPatch);
                  toast.success("Reverted");
                  fetchBookings();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Undo failed");
                }
              },
            },
            duration: 6000,
          });
        }}
      />
      {reassigningBooking && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={() => !reassigning && setReassigningBooking(null)}
          data-testid="reassign-dialog"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full sm:max-w-md bg-[#121212] border border-white/10 rounded-2xl p-5 space-y-4"
          >
            <div>
              <h3 className="text-base font-bold text-white">Reassign trip</h3>
              <p className="text-xs text-zinc-500 mt-1">
                Currently claimed by: <span className="text-zinc-300 font-mono">{reassigningBooking.driver_id || "— unassigned —"}</span>
              </p>
            </div>
            <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="reassign-target-list">
              {reassignTargets.length === 0 && (
                <p className="text-xs text-zinc-500 text-center py-4">Loading drivers…</p>
              )}
              {reassignTargets.map((t) => (
                <button
                  key={t.id}
                  onClick={() => submitReassign(t.id)}
                  disabled={reassigning}
                  data-testid={`reassign-target-${t.id}`}
                  className={`w-full text-left text-sm px-3 py-2 rounded-md border transition-colors ${
                    t.id === reassigningBooking.driver_id
                      ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-300"
                      : "bg-zinc-800/40 border-white/5 text-zinc-200 hover:bg-zinc-800/80"
                  }`}
                >
                  {t.label}
                  {t.id === reassigningBooking.driver_id && (
                    <span className="ml-2 text-[10px] uppercase font-bold">current</span>
                  )}
                </button>
              ))}
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                onClick={() => submitReassign("")}
                disabled={reassigning}
                variant="outline"
                className="flex-1 h-9 border-amber-500/30 text-amber-300 hover:bg-amber-500/10 text-xs"
                data-testid="reassign-clear"
              >
                Unassign (return to pool)
              </Button>
              <Button
                onClick={() => setReassigningBooking(null)}
                disabled={reassigning}
                variant="outline"
                className="flex-1 h-9 border-white/15 text-zinc-300 hover:bg-white/5 text-xs"
                data-testid="reassign-cancel"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
